import React, { useEffect, useMemo, useState } from "react";
import { UserPlus, Search, Mail, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAuth } from "@/contexts/AuthContext";
import { useCompanySelection } from "@/contexts/ActiveCompanyContext";
import { PersonInviteWizard, PersonLinkDialog } from "@/foundation/identity";
import { useNavigate } from "react-router-dom";
import { IdCard, FolderOpen, AlertCircle } from "lucide-react";

const STATUS_LABELS_IT = {
  active: "Attivo",
  invited: "Invitato",
  suspended: "Sospeso",
  disabled: "Disabilitato",
};
const STATUS_TONES = {
  active: "bg-emerald-100 text-emerald-800",
  invited: "bg-blue-100 text-blue-800",
  suspended: "bg-amber-100 text-amber-800",
  disabled: "bg-slate-200 text-slate-700",
};
const ROLE_LABELS_IT = {
  admin: "Root",
  super_admin: "Root",
  consulente: "Consulente",
  company_admin: "Amministratore azienda",
  external_consultant: "Professionista delegato",
  studio_operator: "Collaboratore",
  company_responsible: "Responsabile azienda",
  employee: "Dipendente",
};

// Ordine alfabetico delle denominazioni visibili. Root resta volutamente
// separato e viene mostrato soltanto al gestore globale della Suite.
const ROLE_OPTIONS_ALPHABETICAL = [
  { value: "company_admin", label: "Amministratore azienda" },
  { value: "studio_operator", label: "Collaboratore", superOnly: true },
  { value: "external_consultant", label: "Professionista delegato" },
  { value: "company_responsible", label: "Responsabile azienda" },
];

function RoleSelectOptions({ isSuper, includeAll = false }) {
  return (
    <>
      {isSuper && <SelectItem value="super_admin">Root</SelectItem>}
      {isSuper && <SelectSeparator className="bg-slate-200" />}
      {ROLE_OPTIONS_ALPHABETICAL
        .filter((option) => isSuper || !option.superOnly)
        .map((option) => (
          <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
        ))}
      {includeAll && <SelectSeparator className="bg-slate-200" />}
      {includeAll && <SelectItem value="__all">Tutti i ruoli</SelectItem>}
    </>
  );
}

/**
 * C3-A.3 — Impostazioni · Utenti e accessi
 *
 * Mostra SOLO account gestionali (esclusi employee), esclusi orfani sospesi
 * di default. Ricerca, filtri, paginazione lato backend, ordinamento IT.
 */
export default function AccessManagement() {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const isSuper = currentUser?.role === "admin" || currentUser?.role === "super_admin";
  // Ruoli che possono eseguire azioni mutanti (invite/status/reset/link/unlink)
  const canManage = isSuper
    || currentUser?.role === "studio_operator"
    || currentUser?.role === "client"
    || currentUser?.role === "company_admin";

  const [q, setQ] = useState("");
  const [companyId, setCompanyId] = useCompanySelection();
  const [role, setRole] = useState("__all");
  const [status, setStatus] = useState("__all");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [linkUser, setLinkUser] = useState(null);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (q.trim()) params.q = q.trim();
      if (companyId && companyId !== "__all") params.company_id = companyId;
      if (role && role !== "__all") params.role = role;
      if (status && status !== "__all") params.status = status;
      const { data } = await api.get("/access-management/users", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error("Errore caricamento utenti", { description: e?.response?.data?.detail });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/companies");
        setCompanies(data || []);
      } catch (e) { /* silent */ }
    })();
  }, []);

  useEffect(() => { fetchUsers(); }, [page, q, companyId, role, status, refreshTick]);

  const resetFilters = () => {
    setQ("");
    setRole("__all");
    setStatus("__all");
    setPage(1);
  };

  const changeStatus = async (user, newStatus) => {
    if (!window.confirm(`Confermi il cambio stato di ${user.email} a "${STATUS_LABELS_IT[newStatus]}"?`)) return;
    try {
      await api.post(`/access-management/users/${user.id}/status`, { status: newStatus });
      toast.success("Stato aggiornato");
      fetchUsers();
    } catch (e) {
      toast.error("Errore", { description: e?.response?.data?.detail });
    }
  };

  const sendReset = async (user) => {
    try {
      const { data } = await api.post(`/access-management/users/${user.id}/send-reset`);
      if (data.reset_url) {
        toast.success("Link reset generato", {
          description: `Copiato negli appunti. Scade tra ${data.expires_hours}h`,
          action: {
            label: "Copia link",
            onClick: () => navigator.clipboard.writeText(data.reset_url),
          },
        });
        navigator.clipboard.writeText(data.reset_url);
      } else {
        toast.success("Reset inviato via email");
      }
    } catch (e) {
      toast.error("Errore", { description: e?.response?.data?.detail });
    }
  };

  const resendInvite = async (user) => {
    try {
      const { data } = await api.post(
        `/access-management/users/${user.id}/resend-invite`
      );
      if (data.email_sent) {
        toast.success("Invito reinviato via email");
      } else if (data.invite_url) {
        await navigator.clipboard.writeText(data.invite_url);
        toast.warning("Email non inviata", {
          description: "Il link di attivazione è stato copiato negli appunti.",
        });
      } else {
        toast.error("Invito non inviato", {
          description: data.email_reason || "Provider email non disponibile",
        });
      }
      fetchUsers();
    } catch (e) {
      toast.error("Errore reinvio invito", {
        description: e?.response?.data?.detail,
      });
    }
  };

  const revealUser = (payload = {}) => {
    setQ(payload.email || "");
    setRole("__all");
    setStatus("__all");
    setPage(1);
    if (isSuper && payload.company_id) setCompanyId(payload.company_id);
    setRefreshTick((value) => value + 1);
  };

  const deleteUser = async (user) => {
    if (!window.confirm(
      `Eliminare definitivamente ${user.name || user.email}?\n\n` +
      "L'operazione è consentita soltanto per account non attivi e non elimina dati aziendali."
    )) return;
    try {
      await api.delete(`/access-management/users/${user.id}`);
      toast.success("Utente eliminato");
      setRefreshTick((value) => value + 1);
    } catch (e) {
      toast.error("Eliminazione non riuscita", {
        description: e?.response?.data?.detail,
      });
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-6" data-testid="access-management-root">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Persone e accessi</h2>
          <p className="text-sm text-slate-600">
            Lista delle Persone della Suite (identita' univoca via Codice Fiscale). Ogni riga apre il Fascicolo Persona completo.
          </p>
        </div>
        <Button onClick={() => setInviteOpen(true)} data-testid="invite-user-btn" disabled={!canManage}
                style={{ display: canManage ? undefined : "none" }}>
          <UserPlus className="w-4 h-4 mr-2" /> Invita utente
        </Button>
      </div>

      {/* Banner migrazione legacy: numero Persone senza CF con call-to-action */}
      {(() => {
        const missing = items.filter((u) => !u.fiscal_code && u.account_status === "active").length;
        if (missing === 0) return null;
        return (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 flex flex-wrap items-center gap-3"
                data-testid="legacy-cf-banner">
            <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-amber-900">
                {missing} Person{missing === 1 ? "a" : "e"} in questa pagina senza Codice Fiscale
              </div>
              <div className="text-xs text-amber-800">
                Sono account creati prima della Foundation Identity. Completa il CF per allinearle al nuovo modello.
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                const first = items.find((u) => !u.fiscal_code);
                if (first) setLinkUser(first);
              }}
              className="border-amber-400 bg-white text-amber-800 hover:bg-amber-100"
              data-testid="legacy-cf-banner-cta"
            >
              <IdCard className="mr-1 h-3 w-3" /> Completa la prima
            </Button>
          </div>
        );
      })()}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center bg-slate-50 p-3 rounded-lg border">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
          <Input
            data-testid="access-search-input"
            placeholder="Cerca nome, nome utente o email…"
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            className="pl-9"
          />
        </div>
        {isSuper && (
          <Select value={companyId} onValueChange={(v) => { setCompanyId(v); setPage(1); }}>
            <SelectTrigger className="w-52" data-testid="access-company-filter">
              <SelectValue placeholder="Azienda" />
            </SelectTrigger>
            <SelectContent>
              {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={role} onValueChange={(v) => { setRole(v); setPage(1); }}>
          <SelectTrigger className="w-52" data-testid="access-role-filter">
            <SelectValue placeholder="Ruolo" />
          </SelectTrigger>
          <SelectContent>
            <RoleSelectOptions isSuper={isSuper} includeAll />
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
          <SelectTrigger className="w-40" data-testid="access-status-filter">
            <SelectValue placeholder="Stato" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">Tutti gli stati</SelectItem>
            <SelectItem value="active">Attivo</SelectItem>
            <SelectItem value="invited">Invitato</SelectItem>
            <SelectItem value="suspended">Sospeso</SelectItem>
            <SelectItem value="disabled">Disabilitato</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="ghost" size="sm" onClick={resetFilters} data-testid="access-reset-filters">
          <X className="w-4 h-4 mr-1" /> Azzera filtri
        </Button>
      </div>

      {/* Table with sticky header */}
      <div className="rounded-lg border bg-white overflow-hidden">
        <div className="max-h-[60vh] overflow-y-auto">
          <Table>
            <TableHeader className="sticky top-0 bg-slate-100 z-10">
              <TableRow>
                <TableHead>
                  <IdCard className="inline h-3 w-3 mr-1" /> Codice Fiscale
                </TableHead>
                <TableHead>Cognome e Nome</TableHead>
                <TableHead>Nome utente</TableHead>
                <TableHead>Email accesso</TableHead>
                <TableHead>Ruolo</TableHead>
                <TableHead>Azienda</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-testid="access-users-tbody">
              {loading && (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-500">Caricamento…</TableCell></TableRow>
              )}
              {!loading && items.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-500">Nessuna persona trovata</TableCell></TableRow>
              )}
              {items.map((u) => (
                <TableRow key={u.id} data-testid={`access-user-row-${u.id}`}
                            className={u.person_id ? "cursor-pointer hover:bg-slate-50" : ""}
                            onClick={() => u.person_id && navigate(`/persone/${u.person_id}`)}>
                  <TableCell className="font-mono text-sm">
                    {u.fiscal_code
                      ? <span className="text-slate-900">{u.fiscal_code}</span>
                      : (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); canManage && setLinkUser(u); }}
                          disabled={!canManage}
                          className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-60 disabled:cursor-not-allowed"
                          data-testid={`cf-missing-btn-${u.id}`}
                          title="Codice Fiscale da completare"
                        >
                          <AlertCircle className="h-3 w-3" /> Da completare
                        </button>
                      )}
                  </TableCell>
                  <TableCell className="font-medium">{u.name || "—"}</TableCell>
                  <TableCell className="font-mono text-slate-700">{u.username || "Da assegnare"}</TableCell>
                  <TableCell className="text-slate-700">{u.email}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{ROLE_LABELS_IT[u.role_normalized] || u.role}</Badge>
                  </TableCell>
                  <TableCell className="text-slate-600">
                    {(u.assigned_company_names || []).length > 0
                      ? u.assigned_company_names.join(" · ")
                      : (u.company_name || <span className="text-slate-400">globale</span>)}
                  </TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${STATUS_TONES[u.account_status] || "bg-slate-100"}`}>
                      {STATUS_LABELS_IT[u.account_status] || u.account_status}
                    </span>
                  </TableCell>
                  <TableCell className="text-right space-x-1"
                                onClick={(e) => e.stopPropagation()}>
                    {u.person_id && (
                      <Button size="sm" variant="outline"
                                onClick={() => navigate(`/persone/${u.person_id}`)}
                                data-testid={`open-dossier-btn-${u.id}`}>
                        <FolderOpen className="w-3 h-3 mr-1" /> Fascicolo
                      </Button>
                    )}
                    {!u.person_id && canManage && (
                      <Button size="sm" variant="outline"
                                onClick={() => setLinkUser(u)}
                                className="border-amber-300 text-amber-800 hover:bg-amber-50"
                                data-testid={`complete-cf-btn-${u.id}`}>
                        <IdCard className="w-3 h-3 mr-1" /> Completa CF
                      </Button>
                    )}
                    {canManage && u.account_status === "invited" && (
                      <Button size="sm" variant="outline" onClick={() => resendInvite(u)}
                              data-testid={`resend-invite-btn-${u.id}`}>
                        <Mail className="w-3 h-3 mr-1" /> Reinvia
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {/* Pagination */}
        <div className="flex items-center justify-between p-3 bg-slate-50 border-t">
          <div className="text-sm text-slate-600" data-testid="access-total">
            {total} persone · pagina {page} di {totalPages}
          </div>
          <div className="space-x-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}
                    data-testid="access-page-prev">Precedente</Button>
            <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(page + 1)}
                    data-testid="access-page-next">Successivo</Button>
          </div>
        </div>
      </div>

      <PersonInviteWizard
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        companies={companies}
        defaultCompanyId={isSuper ? undefined : currentUser?.company_id}
        allowedRoles={
          isSuper
            ? undefined
            : (currentUser?.role === "studio_operator"
                ? ["company_admin", "company_responsible", "external_consultant", "employee"]
                : ["company_admin", "company_responsible", "external_consultant"])
        }
        onCreated={() => {
          setRefreshTick((value) => value + 1);
        }}
      />
      <PersonLinkDialog
        open={!!linkUser}
        onOpenChange={(o) => { if (!o) setLinkUser(null); }}
        userId={linkUser?.id}
        suggestedFirstName={(linkUser?.name || "").split(" ").slice(-1)[0] || ""}
        suggestedLastName={(linkUser?.name || "").split(" ").slice(0, -1).join(" ") || ""}
        onLinked={() => { setLinkUser(null); setRefreshTick((v) => v + 1); }}
      />
    </div>
  );
}


